import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";

import { useMediaQuery } from "@/hooks/use-media-query";
import { useSelectedServiceSync } from "@/hooks/use-selected-service-sync";
import { useServiceStore } from "@/stores/service-store";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { AppSidebar } from "./app-sidebar";
import { ServiceLogo } from "./service-selector";
import { SubscriptionBanner } from "./subscription-banner";

interface AppLayoutProps {
  children: React.ReactNode;
}

const isSettingsPath = (pathname: string) =>
  pathname === "/settings" || pathname.startsWith("/settings/");

export function AppLayout({ children }: AppLayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { selectedService } = useServiceStore();
  const { pathname, search } = useLocation();
  const inSettings = isSettingsPath(pathname);
  // Tailwind's md breakpoint. Only one sidebar is mounted at a time: the
  // fixed one on desktop, the drawer's on mobile.
  const isDesktop = useMediaQuery("(min-width: 48rem)");
  useSelectedServiceSync();

  // The last page visited outside settings, where the settings navigation's
  // "Back to app" returns to. Updated while rendering (React's pattern for
  // remembering something from earlier renders), so it is never a step behind.
  const [appPath, setAppPath] = useState("/");
  const currentPath = pathname + search;
  if (!inSettings && appPath !== currentPath) {
    setAppPath(currentPath);
  }

  const [drawerPath, setDrawerPath] = useState(currentPath);
  if (drawerPath !== currentPath) {
    setDrawerPath(currentPath);
    setMobileNavOpen(false);
  }
  // The mobile top bar grows with the subscription banner (which can wrap),
  // so its height goes into a variable that the sticky PageHeader and the
  // main area's min height read.
  const rootRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    const topBar = topBarRef.current;
    if (!root || !topBar) return;
    const observer = new ResizeObserver(() => {
      root.style.setProperty("--top-bar-height", `${topBar.offsetHeight}px`);
    });
    observer.observe(topBar);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--top-bar-height");
    };
  }, [isDesktop]);

  // Closed, not just hidden, once the window is wide enough for the fixed
  // sidebar, so it does not pop open again on the way back to mobile.
  if (isDesktop && mobileNavOpen) {
    setMobileNavOpen(false);
  }

  // Any link followed inside the drawer (nav items, the service and user
  // menus, which portal out but still bubble here) should close it, and so
  // should a control that opens something over the page (data-closes-drawer).
  const closeOnLinkClick = (event: React.MouseEvent) => {
    if ((event.target as Element).closest("a, [data-closes-drawer]")) {
      setMobileNavOpen(false);
    }
  };

  // Radix skips links when picking what to focus in an opening dialog, which
  // in the settings navigation lands on the user menu at the very bottom.
  // Start from the top of the drawer instead.
  const focusFirstItem = (event: Event) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement)
      .querySelector<HTMLElement>("a[href], button")
      ?.focus();
  };

  return (
    <div ref={rootRef} className="min-h-screen overflow-x-clip bg-background">
      {isDesktop && (
        <aside className="fixed inset-y-0 left-0 z-30 w-60 border-r border-sidebar-border">
          <AppSidebar settings={inSettings} backTo={appPath} />
        </aside>
      )}

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        {!isDesktop && (
          // In the drawer the sidebar's subscription card is out of sight, so
          // on mobile the banner sticks to the top of the page instead.
          <div ref={topBarRef} className="sticky top-0 z-30">
            <SubscriptionBanner variant="strip" />
            <header className="flex h-14 items-center gap-2 border-b bg-background/90 px-2 backdrop-blur-sm">
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="flex size-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Open menu"
                >
                  <Menu className="size-5" />
                </button>
              </SheetTrigger>
              {selectedService && (
                <div className="flex min-w-0 items-center gap-2">
                  <ServiceLogo service={selectedService} />
                  <span className="truncate text-sm font-semibold">
                    {selectedService.title}
                  </span>
                </div>
              )}
            </header>
          </div>
        )}

        <SheetContent
          aria-describedby={undefined}
          onClick={closeOnLinkClick}
          onOpenAutoFocus={focusFirstItem}
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <AppSidebar
            settings={inSettings}
            backTo={appPath}
            showSubscriptionBanner={false}
          />
        </SheetContent>
      </Sheet>

      <div className="md:pl-60">
        <main className="flex min-h-[calc(100dvh-var(--top-bar-height,3.5rem))] flex-col px-4 pb-16 sm:px-6 md:min-h-dvh">
          {children}
        </main>
      </div>
    </div>
  );
}
